import * as React from "react";
import { HStack, VStack, Text, Box, Skeleton } from "@chakra-ui/react";
import { parseISO } from "date-fns";
import { ImageUploader } from "components";
import { casesActions } from "configs";
import { useDispatch, useCurrentCase, useParams } from "duck";
import { useUpdatePatientAvatarMutation, Fragments } from "requests";
import { SEX_TO_TITLE } from "../../../../duck";
import { getWeight, getAge } from "../../duck/utils";
import Alert from "../alert";
import { TextField, TextFieldSkeleton } from "./components";

interface AvatarProps {
  patient: Fragments.StoredPatientInfo;
  disclaimerProcedure:
    | Fragments.StoredMedicalCase["disclaimerProcedures"][number]
    | null;
}

const Avatar: React.FC<AvatarProps> = ({ patient, disclaimerProcedure }) => {
  const shouldSync = useCurrentCase(medCase => medCase.shouldSync);

  const { id } = useParams();
  const dispatch = useDispatch();

  const [updateAvatar] = useUpdatePatientAvatarMutation({
    ignoreResults: true
  });

  return (
    <HStack spacing="6" alignItems="flex-start">
      <Box>
        {patient ? (
          <ImageUploader
            label="Avatar"
            labelHidden
            value={patient.image}
            onChange={file => {
              dispatch(
                casesActions.updatePatientImage({ data: file, _id: id })
              );
              if (shouldSync) {
                return updateAvatar({
                  variables: { id: patient._id, image: file || null }
                });
              }
            }}
            bordered={false}
            boxProps={{ border: "none", borderRadius: 4 }}
            imageProps={{ borderRadius: "4px", w: "70px", h: "98px" }}
          />
        ) : (
          <Skeleton width="120px" height="120px" borderRadius="4px" />
        )}
      </Box>

      <VStack spacing="4" align="start">
        <HStack spacing="4" align="center">
          {patient ? (
            <Text color="gray.700" fontSize="lg" fontWeight="bold">
              {patient.name || "-"}
              {disclaimerProcedure && ` (${disclaimerProcedure.title})`}
            </Text>
          ) : (
            <Skeleton height="32px" width="250px" borderRadius="4px" />
          )}
          {patient?.alerts ? <Alert label={patient.alerts} /> : null}
        </HStack>

        {patient ? (
          <HStack spacing="6">
            <TextField
              label="Age"
              value={getAge(
                patient.dateOfBirth ? parseISO(patient.dateOfBirth) : null
              )}
            />
            <TextField
              label="Sex"
              value={patient.sex ? SEX_TO_TITLE[patient.sex] : "-"}
            />
            <TextField
              label="Weight"
              value={getWeight(patient.weight || null, patient.weightMeasure)}
            />
          </HStack>
        ) : (
          <HStack spacing="6">
            <TextFieldSkeleton />
            <TextFieldSkeleton />
            <TextFieldSkeleton />
          </HStack>
        )}
      </VStack>
    </HStack>
  );
};

export default Avatar;
